import React, {
  memo,
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert, Box, Paper, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import api from '../../api/req';
import { getComponentByType } from './editorComponents';
import { actions } from './commandPanels';
import { AppContext } from '../../providers/authProvider';
import WinManagerContext from '../../providers/winManagerProvider';
import { getFrontEndByBackend } from '../../const/meta';
import Loader from '../../components/MaterialUI/loader';
import ErrorShower from './ErrorShower';

function getFields(options, isReadOnly, isNew) {
  if (isReadOnly) return options.actions.GET || {};
  if (!isNew) return options.actions.PUT || {};
  return options.actions.POST || {};
}

function getBaseProps(field) {
  if (!field) return {};
  switch (field.type) {
    case 'string':
      return {
        maxLength: field.max_length || 0,
      };
    case 'choice':
      return {
        values: field.choices || [],
      };
    case 'nested object':
      return {
        columns: field.children,
        backend: field.resource,
      };
    case 'decimal':
      return {
        precision: 2,
      };
    default:
      return {};
  }
}

function getBaseFieldName(field, fields) {
  if (!fields[field]) return '';
  if (fields[field].type === 'nested object' && fields[field].read_only && fields[`${field}_id`]) {
    return `${field}_id`;
  }
  if (fields[field].type === 'field' && fields[field].read_only && fields[`${field}_id`]) {
    return `${field}_id`;
  }
  return field;
}

function SubEditor({
  isLoading, options, modified, err, processAction, data, editorRenderer, getControl,
  CommandPanel, fields, dataSetter,
  erroredFields,
  currentUser,
  reload,
}) {
  return (
    <Box id="editor" sx={{ height: 1, display: 'flex', flexDirection: 'column' }}>
      {isLoading && (
      <Loader text="Завантаження" />
      )}
      <Typography variant="h5" mb={1} color="secondary.main">
        {`${options.name || ''}${modified ? '*' : ''}`}
      </Typography>
      {err && <Alert severity="error">{err}</Alert>}
      <CommandPanel
        onActionClick={processAction}
        isChanged={modified}
        isExecuted={data && data.executed}
        data={data}
      />
      <Paper id="editor2" sx={{ height: 1, overflow: 'auto', p: 2 }}>
        {options && data
                && editorRenderer(getControl, {
                  options,
                  fields,
                  data,
                  dataSetter,
                  erroredFields,
                  currentUser,
                  reload,
                })}
        <ErrorShower fields={fields} erroredFields={erroredFields} />
      </Paper>
    </Box>
  );
}

SubEditor.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  modified: PropTypes.bool.isRequired,
  options: PropTypes.shape().isRequired,
  err: PropTypes.string,
  processAction: PropTypes.func.isRequired,
  data: PropTypes.shape().isRequired,
  editorRenderer: PropTypes.func.isRequired,
  getControl: PropTypes.func.isRequired,
  CommandPanel: PropTypes.func,
  fields: PropTypes.shape({}).isRequired,
  dataSetter: PropTypes.func.isRequired,
  erroredFields: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape().isRequired,
  reload: PropTypes.func.isRequired,
};

SubEditor.defaultProps = {
  err: null,
  CommandPanel: null,
};

const MSubEditor = memo(SubEditor);

const getBaseEditor = (
  editorRenderer,
  backend,
  CommandPanel = () => null,
  privateActionHandler = null,
) => {
  function BaseEditor({ id, url }) {
    const frontend = useMemo(
      () => getFrontEndByBackend(backend),
      [],
    );
    const [isLoading, setIsLoading] = useState(true);
    const [options, setOptions] = useState({
      actions: {
        PUT: {},
      },
    });
    const [data, setData] = useState(null);
    const [controls, setControls] = useState({});
    const [modified, setModified] = useState(false);
    const [err, setErr] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const isNew = (id === 'create');
    const [erroredFields, setErroredFields] = useState({});

    const history = useHistory();
    const { auth, currentUser } = useContext(AppContext);
    const {
      dellComponentFromWindowsManager,
      sendUpdateSignal,
      setWindowTitle,
    } = useContext(WinManagerContext);

    const reload = useCallback(
      () => {
        const loader = async (lUrl, doLoad) => {
          const r = await api.options(lUrl, auth);
          if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
          const result = {};
          result.options = await r.json();
          if (doLoad) {
            const r2 = await api.get(lUrl, auth);
            if (!r2.ok && ![400, 422].includes(r2.status)) throw new Error(`${r2.status} ${r2.statusText}`);
            const d = await r2.json();
            if (r2.ok) {
              result.data = d;
              result.erroredFields = {};
            } else {
              result.data = d;
              result.erroredFields = {};
            }
          } else {
            result.data = {};
            result.erroredFields = {};
          }
          return result;
        };
        setIsLoading(true);
        setErr(null);
        setModified(false);
        setErroredFields({});
        loader(!isNew ? `${backend}${id}/` : backend, !isNew)
          .then((result) => {
            const lisReadOnly = !isNew && !result.options.actions.PUT;
            const fields = getFields(result.options, lisReadOnly, isNew);
            const lcontrols = Object.keys(fields)
              .reduce((R, k) => (
                {
                  ...R,
                  [k]: getComponentByType(
                    fields[k],
                    fields[getBaseFieldName(k, fields)].read_only,
                  ),
                }
              ), {});
            setOptions(result.options);
            setControls(lcontrols);
            setIsReadOnly(lisReadOnly);
            setData(result.data);
          })
          .catch((e) => setErr(e.message))
          .finally(() => setIsLoading(false));
      },
      [auth, id, isNew],
    );

    useEffect(
      () => reload(),
      [reload],
    );

    useEffect(
      () => {
        if (options && options.name) {
          setWindowTitle(url, options.name);
        }
      },
      [options, setWindowTitle, url],
    );

    const fields = useMemo(
      () => getFields(options, isReadOnly, isNew),
      [isNew, isReadOnly, options],
    );

    const dataSetter = useCallback(
      (partialData) => {
        setData((olddata) => ({ ...olddata, ...partialData }));
        setModified(true);
        setErroredFields((efs) => Object.keys(efs)
          .filter((ef) => !(ef in partialData))
          .reduce((R, ef) => ({
            ...R,
            [ef]: erroredFields[ef],
          }), {}));
      },
      [erroredFields],
    );

    const getControl = useCallback(
      (field, newProps = {}) => {
        const Control = controls[field];
        const baseProps = getBaseProps(fields[field]);
        if (!Control) {
          return (
            <Alert severity="error">
              {`Not found coltrol for field "${field}"`}
            </Alert>
          );
        }
        return (
          <Control
            value={data[field]}
            onChange={(e, v) => dataSetter({ [field]: v })}
            label={fields[field].label}
            readOnly={isReadOnly || fields[field].read_only}
            errors={erroredFields[getBaseFieldName(field, fields)]}
            {...baseProps}
            {...newProps}
          />
        );
      },
      [controls, data, dataSetter, erroredFields, fields, isReadOnly],
    );

    const save = useCallback(
      (dd) => {
        const loader = async () => {
          const plainObjectReducer = (obj, flds) => Object.keys(obj)
            .reduce((R, k) => {
              if (!flds[k]) return R;
              if (flds[k].read_only) {
                if (flds[`${k}_id`]) {
                  return { ...R, [`${k}_id`]: obj[k] === null ? null : obj[k].id };
                }
              }
              if ((flds[k].type === 'nested object') && (!k.includes('extension'))) {
                return obj[k] === null ? R : {
                  ...R,
                  [k]: obj[k].id,
                };
              }
              return {
                ...R,
                [k]: obj[k],
              };
            }, {});
          const newStateRoot = plainObjectReducer(dd, fields);
          const newState = Object.keys(newStateRoot).reduce((R, k) => {
            if (fields[k].child && fields[k].child.children && Array.isArray(R[k])) {
              return {
                ...R,
                [k]: R[k].map((row) => plainObjectReducer(row, fields[k].child.children)),
              };
            }
            return R;
          }, newStateRoot);
          const method = isNew ? api.post : api.put;
          const lurl = isNew ? backend : `${backend}${id}/`;
          const r = await method(lurl, auth, newState);
          if (!r.ok && ![400, 422].includes(r.status)) throw new Error(`${r.status} ${r.statusText}`);
          const result = {};
          const d = await r.json();
          if (r.ok) {
            result.data = d;
            result.erroredFields = null;
          } else {
            result.data = newState;
            result.erroredFields = d;
          }
          return result;
        };
        setIsLoading(true);
        loader()
          .then((result) => {
            const ok = !result.erroredFields;
            if (result.erroredFields) {
              setErroredFields(result.erroredFields);
            }
            if (ok) {
              setData(result.data);
              if (isNew) {
                dellComponentFromWindowsManager(url);
                history.push(`${frontend}${result.data.id}`);
              } else {
                reload();
              }
              sendUpdateSignal(backend);
            }
          })
          .catch((e) => setErr(e.message))
          .finally(() => setIsLoading(false));
      },
      [auth, dellComponentFromWindowsManager, fields, frontend, history,
        id, isNew, reload, sendUpdateSignal, url],
    );

    const processAction = useCallback(
      (e, action, ...r) => {
        switch (action) {
          case actions.save:
            save(data);
            break;
          case actions.execute:
            save({ ...data, executed: true });
            break;
          case actions.cancel:
            save({ ...data, executed: false });
            break;
          case actions.close:
            dellComponentFromWindowsManager(url);
            break;
          case actions.reload:
            reload();
            break;
          default:
            if (privateActionHandler) {
              privateActionHandler(e, action, {
                values: { ...r },
                data,
                dataSetter,
              });
            } else {
              console.error('Unknow action', action);
            }
        }
      },
      [data, dataSetter, dellComponentFromWindowsManager, reload, save, url],
    );
    return data && (
      <MSubEditor
        isLoading={isLoading}
        options={options}
        modified={modified}
        err={err}
        processAction={processAction}
        data={data}
        editorRenderer={editorRenderer}
        getControl={getControl}
        CommandPanel={CommandPanel}
        fields={fields}
        dataSetter={dataSetter}
        erroredFields={erroredFields}
        currentUser={currentUser}
        reload={reload}
      />
    );
  }

  BaseEditor.propTypes = {
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  };

  return memo(BaseEditor);
};

export default getBaseEditor;

export const withBaseEditor = (
  WrappedComponent,
  backend,
  CommandPanel = () => null,
  privateActionHandler = null,
) => getBaseEditor((getControl, {
  data,
  dataSetter,
  currentUser,
  options,
  fields,
  erroredFields,
  reload,
}) => (
  <WrappedComponent
    getControl={getControl}
    data={data}
    dataSetter={dataSetter}
    currentUser={currentUser}
    options={options}
    fields={fields}
    erroredFields={erroredFields}
    reload={reload}
  />
), backend, CommandPanel, privateActionHandler);
